import React, { useMemo } from "react";
import {
    useReactTable,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    createColumnHelper,
} from "@tanstack/react-table";
import { formatTimeTo12Hour, formatDateMMDDYYYY } from "../Oncall/utils";

const columnHelper = createColumnHelper();

const PatientManagementTable = ({
    patients,
    isLoading,
    selectedPatientIds = [],
    setSelectedPatientIds,
    onDeletePatient,
    isDeleting,
}) => {
    // Handle select all checkbox
    const handleSelectAll = (checked) => {
        if (checked) {
            const allIds = patients.map((p) => p.patient_id);
            setSelectedPatientIds(allIds);
        } else {
            setSelectedPatientIds([]);
        }
    };

    // Handle individual checkbox
    const handleSelectPatient = (patientId, checked) => {
        if (checked) {
            setSelectedPatientIds((prev) => [...prev, patientId]);
        } else {
            setSelectedPatientIds((prev) => prev.filter((id) => id !== patientId));
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: "select",
                header: ({ table }) => {
                    const allSelected =
                        patients.length > 0 && selectedPatientIds.length === patients.length;
                    const someSelected =
                        selectedPatientIds.length > 0 &&
                        selectedPatientIds.length < patients.length;

                    return (
                        <input
                            type="checkbox"
                            checked={allSelected}
                            ref={(input) => {
                                if (input) input.indeterminate = someSelected;
                            }}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="w-4 h-4 cursor-pointer"
                        />
                    );
                },
                cell: ({ row }) => {
                    const patientId = row.original.patient_id;
                    const isSelected = selectedPatientIds.includes(patientId);

                    return (
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleSelectPatient(patientId, e.target.checked)}
                            className="w-4 h-4 cursor-pointer"
                        />
                    );
                },
                size: 50,
            }),
            columnHelper.accessor("patient_id", {
                header: "Patient ID",
                cell: (info) => (
                    <span className="text-blue-600 font-medium">{info.getValue()}</span>
                ),
                size: 120,
            }),
            columnHelper.accessor("mrn", {
                header: "MRN",
                cell: (info) => info.getValue() || "-",
                size: 120,
            }),
            columnHelper.accessor("appointment_id", {
                header: "Appointment ID",
                cell: (info) => info.getValue() || "-",
                size: 150,
            }),
            columnHelper.accessor("appt_time", {
                header: "Time",
                cell: (info) => {
                    const value = info.getValue();
                    return value ? formatTimeTo12Hour(value) : "-";
                },
                size: 100,
            }),
            columnHelper.accessor("status", {
                header: "Status",
                cell: (info) => {
                    const status = info.getValue();
                    const statusColors = {
                        Locked: "bg-green-100 text-green-800",
                        Review: "bg-yellow-100 text-yellow-800",
                        Processing: "bg-blue-100 text-blue-800",
                        Failed: "bg-red-100 text-red-800",
                    };
                    const colorClass = statusColors[status] || "bg-gray-100 text-gray-800";

                    return status ? (
                        <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}
                        >
                            {status}
                        </span>
                    ) : (
                        "-"
                    );
                },
                size: 120,
            }),
            columnHelper.accessor("ehr_upload_status", {
                header: "EHR Upload",
                cell: (info) => {
                    const status = info.getValue();
                    return status ? (
                        <span className="text-xs">{status}</span>
                    ) : (
                        "-"
                    );
                },
                size: 120,
            }),
            columnHelper.accessor("description", {
                header: "Description",
                cell: (info) => {
                    const desc = info.getValue();
                    return desc ? (
                        <span className="text-xs truncate max-w-[200px] block" title={desc}>
                            {desc}
                        </span>
                    ) : (
                        "-"
                    );
                },
                size: 200,
            }),
            columnHelper.display({
                id: "actions",
                header: "Actions",
                cell: ({ row }) => {
                    const patientId = row.original.patient_id;

                    return (
                        <button
                            onClick={() => onDeletePatient(patientId)}
                            disabled={isDeleting}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Delete
                        </button>
                    );
                },
                size: 100,
            }),
        ],
        [patients, selectedPatientIds, isDeleting]
    );

    const table = useReactTable({
        data: patients || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (isLoading) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600">Loading patients...</p>
            </div>
        );
    }

    if (!patients || patients.length === 0) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600">
                    No patients found for this doctor and date.
                </p>
            </div>
        );
    }

    return (
        <div className="w-full">
            {/* Table */}
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        style={{ width: header.getSize() }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {table.getRowModel().rows.map((row) => {
                            const isSelected = selectedPatientIds.includes(
                                row.original.patient_id
                            );

                            return (
                                <tr
                                    key={row.id}
                                    className={`hover:bg-gray-50 ${isSelected ? "bg-blue-50" : ""}`}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="px-4 py-3 text-sm text-gray-900">
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Patient count */}
            <div className="mt-4 text-sm text-gray-700">
                Showing {patients.length} patient{patients.length !== 1 ? "s" : ""}
            </div>
        </div>
    );
};

export default PatientManagementTable;
